import type { Database } from '@planmax/shared-types'
import type { BadgeProps } from '@/components/ui/badge'

// ============================================
// Enums from Database
// ============================================

export type ContributionSource = Database['public']['Enums']['contribution_source']
export type ContributionStatus = Database['public']['Enums']['contribution_status']

// ============================================
// Enum Arrays for Iteration
// ============================================

export const CONTRIBUTION_SOURCES: ContributionSource[] = [
  'aircraft_away',
  'empty_lease_spot',
  'permanent_free',
]

export const CONTRIBUTION_STATUSES: ContributionStatus[] = [
  'projected',
  'confirmed',
  'consumed',
  'cancelled',
]

// ============================================
// Enum Labels
// ============================================

export const CONTRIBUTION_SOURCE_LABELS: Record<ContributionSource, string> = {
  aircraft_away: 'Aircraft Away',
  empty_lease_spot: 'Empty Lease Spot',
  permanent_free: 'Permanent Free',
}

export const CONTRIBUTION_STATUS_LABELS: Record<ContributionStatus, string> = {
  projected: 'Projected',
  confirmed: 'Confirmed',
  consumed: 'Consumed',
  cancelled: 'Cancelled',
}

// ============================================
// Badge Variants for Status
// ============================================

type BadgeVariant = BadgeProps['variant']

export const CONTRIBUTION_STATUS_VARIANTS: Record<ContributionStatus, BadgeVariant> = {
  projected: 'secondary',
  confirmed: 'default',
  consumed: 'outline',
  cancelled: 'destructive',
}

export const CONTRIBUTION_SOURCE_VARIANTS: Record<ContributionSource, BadgeVariant> = {
  aircraft_away: 'secondary',
  empty_lease_spot: 'secondary',
  permanent_free: 'secondary',
}

// ============================================
// Database Row Types
// ============================================

export type SpaceContributionRow = Database['public']['Tables']['space_contributions']['Row']
export type SpaceContributionInsert = Database['public']['Tables']['space_contributions']['Insert']
export type SpaceContributionUpdate = Database['public']['Tables']['space_contributions']['Update']

// ============================================
// Extended Type with Relations
// ============================================

export interface SpaceContributionWithRelations extends SpaceContributionRow {
  // Joined unit data
  unit_name?: string | null
  hangar_name?: string | null

  // Joined aircraft data
  tail_number?: string | null
  aircraft_type_name?: string | null

  // Joined flight_schedule data
  flight_status?: string | null
  departure_datetime?: string | null
  arrival_datetime?: string | null
}

// ============================================
// Table Row Type (for list display)
// ============================================

export interface SpaceContributionTableRow {
  id: string
  organization_id: string
  location_id: string
  unit_id: string
  source_type: ContributionSource
  unit_name?: string | null
  hangar_name?: string | null
  aircraft_id: string | null
  tail_number?: string | null
  aircraft_type_name?: string | null
  flight_schedule_id: string | null
  lease_contract_id: string | null
  available_from: string
  available_until: string
  sqft: number
  wingspan: number | null
  length: number | null
  status: ContributionStatus
  created_at: string
  updated_at: string | null
}

// ============================================
// Form Data Type
// ============================================

export interface SpaceContributionFormData {
  // Required
  unit_id: string
  source_type: ContributionSource
  available_from: string
  available_until: string
  sqft: number
  status: ContributionStatus

  // Optional
  aircraft_id?: string | null
  flight_schedule_id?: string | null
  lease_contract_id?: string | null
  wingspan?: number | null
  length?: number | null
  position_x?: number | null
  position_y?: number | null
}

// ============================================
// Filter Type
// ============================================

export interface SpaceContributionFilters {
  search?: string
  sourceType?: ContributionSource | null
  status?: ContributionStatus | null
  unitId?: string | null
  dateFrom?: string
  dateTo?: string
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format date/time for display
 */
export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format date only for display
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Get display name for space contribution
 */
export function getSpaceContributionDisplayName(
  contribution: Pick<
    SpaceContributionWithRelations,
    'source_type' | 'unit_name' | 'tail_number' | 'available_from'
  >
): string {
  const unit = contribution.unit_name || '—'
  const source = CONTRIBUTION_SOURCE_LABELS[contribution.source_type]
  const aircraft = contribution.tail_number ? ` (${contribution.tail_number})` : ''
  const date = formatDate(contribution.available_from)
  return `${unit} - ${source}${aircraft} - ${date}`
}

/**
 * Check if contribution is in the past
 */
export function isPastContribution(availableUntilStr: string): boolean {
  const availableUntil = new Date(availableUntilStr)
  return availableUntil < new Date()
}

/**
 * Check if contribution is currently available
 */
export function isCurrentContribution(
  availableFromStr: string,
  availableUntilStr: string
): boolean {
  const now = new Date()
  const availableFrom = new Date(availableFromStr)
  const availableUntil = new Date(availableUntilStr)
  return now >= availableFrom && now <= availableUntil
}

/**
 * Calculate available duration in days
 */
export function calculateAvailableDays(
  availableFromStr: string,
  availableUntilStr: string
): number {
  const from = new Date(availableFromStr)
  const until = new Date(availableUntilStr)
  const diffMs = until.getTime() - from.getTime()
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}
